import { useEffect, useRef } from 'react';

import { FieldValues } from 'react-hook-form';

const isAnswered = (value: unknown) =>
  Array.isArray(value)
    ? value.length > 0
    : value !== '' && value !== null && value !== undefined && value !== false;

// Ecommerce's form starts every field pre-populated via ECOMMERCE_DEFAULT_VALUES
// (schema.ts), unlike v2's sparse per-answer API data, so a "new field key
// appeared" diff (what v2's useScrollToStep relies on) never fires here.
// Instead, scroll whenever one of the given gating fields — the ones a
// ConditionalRender keys off to reveal the next question — flips from
// unanswered to answered.
//
// react-hook-form's watch() doesn't reliably return a new object reference
// per render, so a useEffect keyed on the whole `values` object can miss
// changes. Snapshotting just the gating fields into a JSON string gives
// useEffect a primitive to diff against instead.
const useScrollToNextField = (
  values: FieldValues,
  gatingFieldNames: string[]
) => {
  const ref = useRef<HTMLDivElement>(null);
  const snapshot = gatingFieldNames.map((name) => values[name]);
  const snapshotKey = JSON.stringify(snapshot);
  const prevSnapshot = useRef<unknown[]>(snapshot);

  useEffect(() => {
    const justAnswered = snapshot.some(
      (value, i) => !isAnswered(prevSnapshot.current[i]) && isAnswered(value)
    );

    const OFFSET = 500;

    if (ref.current && justAnswered) {
      const targetScroll = ref.current.offsetTop - OFFSET;
      if (targetScroll > 100) {
        setTimeout(() => {
          window.scrollTo({ top: targetScroll, behavior: 'smooth' });
        }, 100);
      }
    }

    prevSnapshot.current = snapshot;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotKey]);

  return { ref };
};

export default useScrollToNextField;
